import { Controller } from '@hotwired/stimulus'

import { useLocalTimeClock } from '../src/mixins/cesium/use_local_time_clock'
import { useZoomToEntities } from '../src/mixins/cesium/use_zoom_to_entities'

import { withAircraftLayer } from '../src/mixins/cesium/with_aircraft_layer'
import { withCompass } from '../src/mixins/cesium/with_compass'
import { withDockMarkers } from '../src/mixins/cesium/with_dock_markers'
import { withDroneMarkers } from '../src/mixins/cesium/with_drone_markers'
import { withHubMarkers } from '../src/mixins/cesium/with_hub_markers'
import { withLiveCesiumMap } from '../src/mixins/cesium/with_cesium_map'

export default class extends Controller {
  static values = {
    accessToken: String,
    docks: Array, // Array of { name: String, serial_number: String, position: { latitude: Number, longitude: Number } }
    drones: Array, // Array of { name: String, serial_number: String, position: { latitude: Number, longitude: Number }, elevation: Number }
    hubs: Array // Array of { name: String, serial_number: String, position: { latitude: Number, longitude: Number } }
  }

  connect () {
    // Basic Functions
    this.viewer = withLiveCesiumMap(this, {
      accessToken: this.accessTokenValue
    })

    useZoomToEntities(this, { cesiumViewer: this.viewer })
    useLocalTimeClock(this, { cesiumViewer: this.viewer })

    // Add ADS-B Layer and Data Source Table
    this.aircraftLayer = withAircraftLayer(this, { cesiumViewer: this.viewer })

    // Add all Docks as Markers
    this.dockEntity = null
    this.dockMarkers = withDockMarkers(this, { cesiumViewer: this.viewer })
    for (const dockData of this.docksValue) {
      this.dockMarkers.update(dockData)
    }

    // Add all Drones as Markers
    this.droneEntity = null
    this.droneMarkers = withDroneMarkers(this, { cesiumViewer: this.viewer })
    for (const droneData of this.dronesValue) {
      this.droneMarkers.update(droneData, Date.now())
    }

    // Add all Hubs as Markers
    this.hubEntity = null
    this.hubMarkers = withHubMarkers(this, { cesiumViewer: this.viewer })
    for (const hubData of this.hubsValue) {
      this.hubMarkers.update(hubData)
    }

    // Add Compass
    withCompass(this, { cesiumViewer: this.viewer })

    // Zoom to the first Hub and Orbit
    const coordinates = this.hubMarkers.getCoordinates()
    if (coordinates) this.zoomToAndOrbitCoordinates(coordinates)

    // Refresh the separation targets
    this.refreshSeparations()
  }

  startPlayback () {
    if (this.earliestTimestamp) {
      // start the clock animation
      this.viewer.clock.shouldAnimate = true
    }
  }

  // Update the Drone Position
  // eslint-disable-next-line camelcase
  updateDrone ({ detail: { drone_status, meta: { last_timestamp } } }) {
    // Ignore if the drone time is too far in the past
    // eslint-disable-next-line camelcase
    const diffInSeconds = Math.abs((new Date(this.viewer.clock.currentTime).getTime() - last_timestamp) / 1000)
    if (diffInSeconds > 60) return

    // Redraw the Drone Position
    // eslint-disable-next-line camelcase
    this.droneMarkers.update(drone_status, last_timestamp).then(() => {
      // Update the Drone Entity if we haven't already
      if (!this.droneEntity) {
        this.droneEntity = this.droneMarkers.getEntity()
        this.refreshSeparations()
      }
    })
  }

  // eslint-disable-next-line camelcase
  updateDock ({ detail: { dock_status } }) {
    // Update the Dock Position
    this.dockMarkers.update(dock_status)

    // Update the Separation Target if we haven't already
    if (!this.dockEntity) {
      this.dockEntity = this.dockMarkers.getEntity()
      this.refreshSeparations()
    }
  }

  // eslint-disable-next-line camelcase
  updateHub ({ detail: { hub_x_status } }) {
    // Update the Hub Position
    this.hubMarkers.update(hub_x_status)

    if (!this.hubEntity) {
      // Update the Separation Target if we haven't already
      this.hubEntity = this.hubMarkers.getEntity()
      this.refreshSeparations()

      // Zoom to the Hub and Orbit
      const hubCoordinates = this.hubMarkers.getCoordinates()
      if (hubCoordinates) this.zoomToAndOrbitCoordinates(hubCoordinates)
    }
  }

  refreshSeparations () {
    const separationEntity = this.hubEntity || this.dockEntity || this.droneEntity
    if (!separationEntity) return

    this.aircraftLayer.setSeparationTarget(separationEntity)
  }
}
